import { useEffect, useId, type ReactNode } from "react"
import { Button } from "@/shared/ui/Button"
import { Close } from "@/shared/ui/icons"

export interface ModalProps {
  open: boolean
  onClose: () => void
  onConfirm?: () => void
  title?: string
  children: ReactNode
  confirmLabel?: string
  cancelLabel?: string
}

export function Modal({
  open,
  onClose,
  onConfirm,
  title = "Você será direcionado para continuar",
  children,
  confirmLabel = "Continuar",
  cancelLabel = "Voltar para a página",
}: ModalProps) {
  const titleId = useId()

  useEffect(() => {
    if (!open) return

    // Trava o scroll do fundo e restaura o valor anterior — atribuir "" na
    // limpeza apagaria um overflow que outra parte da pagina tenha definido.
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        className="absolute inset-0 bg-verde-profundo/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 flex flex-col gap-6">
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-verde-profundo/50 hover:text-verde-profundo rounded-lg transition-colors cursor-pointer"
        >
          <Close size={16} />
        </button>

        <div className="flex flex-col gap-3">
          <h2 id={titleId} className="text-xl font-semibold text-verde-profundo leading-snug">
            {title}
          </h2>
          <div className="text-verde-profundo/75 text-base leading-relaxed">{children}</div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="primary" size="md" className="flex-1" onClick={onConfirm ?? onClose}>
            {confirmLabel}
          </Button>
          <Button variant="secondary" size="md" className="flex-1" onClick={onClose}>
            {cancelLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
